import React, { useEffect, useRef, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { LensCatalogueField } from '~/types/leLens';

import {
  catalogueFieldCombineWord,
  catalogueFieldsByName,
  catalogueRatioPillLabel,
  hasUndeclaredFlag,
  highlightRuns,
  splitMeasureFields,
} from './catalogueFormat';

/** Rows shown before "Show all N" reveals the rest. Dates never has enough to need it. */
const PREVIEW_COUNT = 6;

/** The description clamp's line-height, in em, shared by the `-webkit-line-clamp` declaration and
 *  the plain-CSS `maxHeight` backstop below so the two always agree on where 2 lines end. */
const DESC_LINE_HEIGHT_EM = 1.45;
const DESC_CLAMP_LINES = 2;

function HighlightedText({ query, text }: { text: string; query?: string }) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  if (!query || !query.trim()) {
    return <>{text}</>;
  }
  return (
    <>
      {highlightRuns(text, query).map((run, index) =>
        run.matched ? (
          <Box component="mark" key={index} sx={{ bgcolor: alpha(tokens.accent, 0.18), borderRadius: '2px', color: 'inherit', px: '1px' }}>
            {run.text}
          </Box>
        ) : (
          <React.Fragment key={index}>{run.text}</React.Fragment>
        ),
      )}
    </>
  );
}

/** The same 8px ring-dot used on the hero: structural ring, Coral Green centre. A field-level
 *  certified flag does not exist on `LensCatalogueField` yet; this renders once the API adds one. */
function CertifiedDot() {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  return (
    <Box
      aria-label="Certified"
      role="img"
      sx={{
        alignItems: 'center',
        border: `1.5px solid ${tokens.accent}`,
        borderRadius: '50%',
        display: 'inline-flex',
        flexShrink: 0,
        height: 8,
        justifyContent: 'center',
        width: 8,
      }}
    >
      <Box sx={{ bgcolor: tokens.coralAccent, borderRadius: '50%', height: 4, width: 4 }} />
    </Box>
  );
}

function FieldRow({
  certified,
  field,
  highlightQuery,
  onClick,
  rightLabel,
  selected,
}: {
  field: LensCatalogueField;
  onClick?: (field: LensCatalogueField) => void;
  rightLabel?: string;
  highlightQuery?: string;
  certified?: boolean;
  /** True while this field's drawer is open. 2px accent left rule + a permanent tint, cleared the
   *  moment the drawer closes (the parent simply stops passing this field's name down). */
  selected?: boolean;
}) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const interactive = Boolean(onClick);
  const descRef = useRef<HTMLParagraphElement>(null);
  const [descExpanded, setDescExpanded] = useState(false);
  const [descTruncated, setDescTruncated] = useState(false);

  // Measured once on mount (and when the description text itself changes): correct for the
  // description's own wrapping, not re-checked on a later viewport resize.
  useEffect(() => {
    const element = descRef.current;
    if (!element || descExpanded) {
      return;
    }
    setDescTruncated(element.scrollHeight > element.clientHeight + 1);
  }, [field.description, descExpanded]);

  const activate = () => onClick?.(field);

  const body = (
    <Stack alignItems="flex-start" direction="row" justifyContent="space-between" spacing={1.5} sx={{ minWidth: 0, py: 1.25 }}>
      <Box sx={{ minWidth: 0 }}>
        <Stack alignItems="center" direction="row" spacing={0.75}>
          <Typography sx={{ fontSize: '15px', fontWeight: 500 }}>
            <HighlightedText query={highlightQuery} text={field.label} />
          </Typography>
          {certified && <CertifiedDot />}
        </Stack>
        {field.description && (
          <>
            <Typography
              color="text.secondary"
              component="p"
              ref={descRef}
              sx={{
                display: '-webkit-box',
                fontSize: '13px',
                lineHeight: `${DESC_LINE_HEIGHT_EM}em`,
                // `-webkit-line-clamp` gives the proper ellipsis-style cut at a line boundary in
                // Chromium; `maxHeight` + `overflow: hidden` is a plain-CSS backstop that hard-crops
                // the box at exactly DESC_CLAMP_LINES lines regardless of clamp support, so a long
                // description can never bleed into the next field row.
                maxHeight: descExpanded ? 'none' : `${DESC_LINE_HEIGHT_EM * DESC_CLAMP_LINES}em`,
                mt: 0.25,
                overflow: descExpanded ? 'visible' : 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: descExpanded ? 'unset' : DESC_CLAMP_LINES,
              }}
            >
              <HighlightedText query={highlightQuery} text={field.description} />
            </Typography>
            {descTruncated && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.25 }}>
                <Box
                  component="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setDescExpanded((value) => !value);
                  }}
                  onKeyDown={(event) => event.stopPropagation()}
                  sx={{
                    background: 'none',
                    border: 0,
                    color: tokens.accent,
                    cursor: 'pointer',
                    font: 'inherit',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    p: 0,
                  }}
                  type="button"
                >
                  {descExpanded ? 'less' : 'more'}
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
      <Stack alignItems="center" direction="row" spacing={0.75} sx={{ flexShrink: 0, pt: 0.2 }}>
        {rightLabel && (
          <Box
            sx={{
              border: `1px solid ${alpha(tokens.accent, 0.3)}`,
              borderRadius: 999,
              color: tokens.accent,
              fontSize: '10.5px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              px: 1,
              py: 0.25,
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}
          >
            {rightLabel}
          </Box>
        )}
        {interactive && <ChevronRightIcon color="action" fontSize="small" />}
      </Stack>
    </Stack>
  );

  if (!interactive) {
    return <Box sx={{ px: 1.25 }}>{body}</Box>;
  }

  return (
    <Box
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          activate();
        }
      }}
      role="button"
      sx={{
        bgcolor: selected ? alpha(tokens.accent, 0.04) : 'transparent',
        borderLeft: `2px solid ${selected ? tokens.accent : 'transparent'}`,
        borderRadius: 1,
        cursor: 'pointer',
        pl: 1,
        pr: 1.25,
        '&:focus-visible': { outline: `2px solid ${tokens.accent}`, outlineOffset: '-2px' },
        '&:hover': { bgcolor: alpha(tokens.accent, 0.04) },
      }}
      tabIndex={0}
    >
      {body}
    </Box>
  );
}

function FieldGroup({
  count,
  expandAll = false,
  fields,
  highlightQuery,
  onFieldClick,
  ratioSentences,
  selectedFieldName,
  title,
}: {
  title: string;
  count: number;
  fields: Array<LensCatalogueField>;
  onFieldClick?: (field: LensCatalogueField) => void;
  ratioSentences?: ReadonlyMap<string, LensCatalogueField>;
  highlightQuery?: string;
  expandAll?: boolean;
  selectedFieldName?: string;
}) {
  const [manuallyExpanded, setManuallyExpanded] = useState(false);
  const theme = useTheme();
  const tokens = lensTokens(theme);
  if (fields.length === 0) {
    return null;
  }
  const expanded = expandAll || manuallyExpanded;
  const visible = expanded ? fields : fields.slice(0, PREVIEW_COUNT);
  const hiddenCount = fields.length - visible.length;

  return (
    <Box
      sx={{
        bgcolor: tokens.surface,
        border: `1px solid ${alpha(tokens.accent, 0.12)}`,
        borderRadius: 1,
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        sx={{ borderBottom: `1px solid ${alpha(tokens.accent, 0.12)}`, px: 1.5, py: 1.125 }}
      >
        <Typography
          sx={{ color: tokens.accent, fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            border: `1px solid ${alpha(tokens.accent, 0.3)}`,
            borderRadius: 999,
            color: tokens.accent,
            fontSize: '11px',
            fontWeight: 700,
            minWidth: 22,
            px: 0.75,
            py: 0.125,
            textAlign: 'center',
          }}
        >
          {count}
        </Box>
      </Stack>
      <Stack divider={<Box sx={{ borderBottom: `1px solid ${alpha(tokens.accent, 0.12)}` }} />} spacing={0} sx={{ p: 0.5 }}>
        {visible.map((field) => (
          <FieldRow
            certified={hasUndeclaredFlag(field, 'certified')}
            field={field}
            highlightQuery={highlightQuery}
            key={field.name}
            onClick={onFieldClick}
            rightLabel={
              ratioSentences ? (catalogueRatioPillLabel(field, ratioSentences) ?? '') : catalogueFieldCombineWord(field)
            }
            selected={field.name === selectedFieldName}
          />
        ))}
      </Stack>
      {hiddenCount > 0 && (
        <Typography
          component="button"
          onClick={() => setManuallyExpanded(true)}
          sx={{
            background: 'none',
            border: 0,
            color: tokens.accent,
            cursor: 'pointer',
            display: 'block',
            font: 'inherit',
            fontWeight: 600,
            mb: 1.125,
            ml: 1.5,
            mt: 0.75,
            p: 0,
          }}
          type="button"
          variant="caption"
        >
          Show all {fields.length}
        </Typography>
      )}
    </Box>
  );
}

export type DatasetFieldGroupsProps = {
  fields: Array<LensCatalogueField>;
  onFieldClick?: (field: LensCatalogueField) => void;
  /** Wraps matches of this query in each row's label and description. Optional; the Lens picker's
   *  unfiltered "See full details" use omits it and renders plain text, exactly as before. */
  highlightQuery?: string;
  /** Lifts the "Show all N" cap so every row in the (already filtered) `fields` list renders.
   *  Optional, defaults false, so existing callers keep the PREVIEW_COUNT behaviour unchanged. */
  expandAll?: boolean;
  /**
   * Resolves a ratio measure's "of ÷ per" pill even when `fields` has been filtered down and no
   * longer includes the referenced field. Defaults to a map built from `fields` itself, correct
   * whenever the caller passes the dataset's complete, unfiltered field list.
   */
  allFieldsByName?: ReadonlyMap<string, LensCatalogueField>;
  /** The field whose drawer is currently open, by raw name. That row gets a 2px accent left rule
   *  plus a permanent tint; omit (or pass undefined once the drawer closes) to clear it. */
  selectedFieldName?: string;
};

/**
 * A governed dataset's fields, grouped Dates, Dimensions, Measures and Standard measures (the
 * ratio sub-group), the same layout the Catalogue's detail screen and the picker's "See full
 * details" expansion both use. `onFieldClick` opens the field drawer; omit it for the picker,
 * which has nowhere for a drawer to sit.
 */
export default function DatasetFieldGroups({
  allFieldsByName,
  expandAll = false,
  fields,
  highlightQuery,
  onFieldClick,
  selectedFieldName,
}: DatasetFieldGroupsProps) {
  const dates = fields.filter((field) => field.kind === 'date');
  const dimensions = fields.filter((field) => field.kind === 'dimension');
  const { plain: measures, standard } = splitMeasureFields(fields);
  const fieldsByName = allFieldsByName ?? catalogueFieldsByName(fields);

  return (
    <Grid container spacing={2}>
      <Grid size={{ md: 6, xs: 12 }}>
        <Stack spacing={2}>
          <FieldGroup
            count={dates.length}
            expandAll={expandAll}
            fields={dates}
            highlightQuery={highlightQuery}
            onFieldClick={onFieldClick}
            selectedFieldName={selectedFieldName}
            title="Dates"
          />
          <FieldGroup
            count={dimensions.length}
            expandAll={expandAll}
            fields={dimensions}
            highlightQuery={highlightQuery}
            onFieldClick={onFieldClick}
            selectedFieldName={selectedFieldName}
            title="Dimensions"
          />
        </Stack>
      </Grid>
      <Grid size={{ md: 6, xs: 12 }}>
        <Stack spacing={2}>
          <FieldGroup
            count={measures.length}
            expandAll={expandAll}
            fields={measures}
            highlightQuery={highlightQuery}
            onFieldClick={onFieldClick}
            selectedFieldName={selectedFieldName}
            title="Measures"
          />
          <FieldGroup
            count={standard.length}
            expandAll={expandAll}
            fields={standard}
            highlightQuery={highlightQuery}
            onFieldClick={onFieldClick}
            ratioSentences={fieldsByName}
            selectedFieldName={selectedFieldName}
            title="Standard Measures"
          />
        </Stack>
      </Grid>
    </Grid>
  );
}
